// EmailDialog.tsx

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Box,
  Typography,
  Checkbox,
  useTheme,
  Alert,
  AlertTitle,
} from "@mui/material";
import { MailInfo } from "IF/interfaceType";
import { VerificationType } from "./SettingMail";

// コンポーネントの Props の型
type NewMailDialogProps = {
  open: boolean;
  handleClose: () => void;
  handleAddEmail: () => void;
  newEmail: MailInfo;
  setNewEmail: React.Dispatch<React.SetStateAction<MailInfo>>;
  handleVerify: (type: VerificationType, contact: string) => void;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  newMailCheckboxChange: (
    field: keyof MailInfo
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const SettingNewMailDialog: React.FC<NewMailDialogProps> = (props) => {
  const {
    open,
    handleClose,
    handleAddEmail,
    newEmail,
    setNewEmail,
    handleVerify,
    isEmailVerified,
    isPhoneVerified,
    message,
    setMessage,
    newMailCheckboxChange,
  } = props;

  const theme = useTheme();

  const canRegister =
    (newEmail.mailAddress.trim() !== "" && !isEmailVerified) ||
    (newEmail.telNumber?.trim() !== "" && !isPhoneVerified);

  return (
    <Dialog open={open} onClose={handleClose}>
      <Box sx={{ backgroundColor: theme.palette.custom.sForm }}>
        <DialogTitle sx={{ pb: 1 }}>配信情報を登録</DialogTitle>
        <DialogContent sx={{ pb: 0.5 }}>
          <Box
            sx={{
              mb: 0.5,
              width: 350,
              height: 80,
              // minHeight: theme.spacing(6) /* ≒48px 分だけ余白を確保 */,
            }}
          >
            {message ? (
              <Alert
                sx={{
                  width: 350,
                  height: 80,
                  fontSize: "0.7rem",
                }}
                severity="warning"
                onClose={() => setMessage("")}
              >
                <AlertTitle sx={{ fontSize: "0.75rem", mb: 0.2 }}>
                  Message
                </AlertTitle>
                {message}
              </Alert>
            ) : (
              // 空時でもこのボックスが高さをキープしてくれる
              <Alert
                // s
                sx={{
                  width: 350,
                  height: 80,
                  fontSize: "0.7rem",
                }}
              >
                <AlertTitle sx={{ fontSize: "0.75rem", mb: 0.2 }}>
                  Message
                </AlertTitle>
              </Alert>
            )}
          </Box>
          <Stack direction="column" spacing={1.5} padding={1}>
            {/* 氏名入力欄 */}
            <TextField
              autoFocus
              margin="dense"
              label="氏名"
              type="text"
              fullWidth
              variant="outlined"
              value={newEmail.mailName}
              onChange={(e) =>
                setNewEmail((prev) => ({
                  ...prev,
                  mailName: e.target.value,
                }))
              }
            />

            {/* メール 枠線 */}
            <Box
              sx={{
                border: "2px solid #E68B57",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {/* タイトル部：メール */}
              <Box
                sx={{
                  borderBottom: "1px solid #E68B57",
                  backgroundColor: "#E68B57",
                  color: "#333333",
                  p: 1,
                }}
              >
                <Typography fontWeight="fontWeightMedium" variant="body2">
                  メール
                </Typography>
              </Box>

              {/* コンテンツ部：アラート配信、CSV配信、メールアドレス */}
              <Box sx={{ p: 1 }}>
                {/* アラート配信グループとCSV配信グループ */}
                <Box display="flex" alignItems="center">
                  {/* アラート配信グループ */}
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 0 }}>
                      警報：
                    </Typography>
                    <Checkbox
                      checked={newEmail.alertCheckbox}
                      size="small"
                      sx={{ p: 0 }}
                      disabled={!isEmailVerified}
                      onChange={newMailCheckboxChange("alertCheckbox")}
                    />
                  </Box>
                  <Box sx={{ mx: 0.5 }} />
                  {/* 通信不可グループ */}
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 0 }}>
                      通信不可：
                    </Typography>
                    <Checkbox
                      checked={newEmail.connectCheckbox}
                      size="small"
                      sx={{ p: 0 }}
                      disabled={!isEmailVerified}
                      onChange={newMailCheckboxChange("connectCheckbox")}
                    />
                  </Box>
                  <Box sx={{ mx: 1 }} />
                </Box>
                <Box display="flex" alignItems="center">
                  {/* メールアドレス入力欄 */}
                  <TextField
                    margin="dense"
                    label="メールアドレス"
                    type="email"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={newEmail.mailAddress}
                    onChange={(e) => {
                      // 許可する文字：英数字 + よく使われるメールアドレス系記号 + @ + . (ピリオド)
                      const allowedChars =
                        /[^0-9A-Za-z!#$%&'*+\-/=?^_`{|}~.@]/g;

                      // 不許可の文字があれば削除
                      const cleanedValue = e.target.value.replace(
                        allowedChars,
                        ""
                      );

                      setNewEmail((prev) => ({
                        ...prev,
                        mailAddress: cleanedValue,
                      }));
                    }}
                    disabled={isEmailVerified}
                    sx={{ mt: 1 }}
                  />
                  <Button
                    onClick={() => handleVerify("email", newEmail.mailAddress)}
                    variant="contained"
                    sx={{ ml: 1 }}
                    size="small"
                    disabled={isEmailVerified || newEmail.mailAddress === ""}
                  >
                    {isEmailVerified ? "認証済" : "認証"}
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* SMS*/}
            <Box
              sx={{
                border: "2px solid #E68B57",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {/* タイトル部：SMS */}
              <Box
                sx={{
                  borderBottom: "1px solid #E68B57",
                  backgroundColor: "#E68B57",
                  color: "#333333",
                  p: 1,
                }}
              >
                <Typography fontWeight="fontWeightMedium" variant="body2">
                  SMS
                </Typography>
              </Box>

              {/* コンテンツ部：SMS */}
              <Box sx={{ p: 1 }}>
                {/* アラート配信グループとCSV配信グループ */}
                <Box display="flex" alignItems="center">
                  <Typography variant="body2">警報：</Typography>
                  <Checkbox
                    checked={newEmail.smsCheckbox}
                    sx={{ p: 0 }}
                    disabled={!isPhoneVerified}
                    size="small"
                    onChange={newMailCheckboxChange("smsCheckbox")}
                  />
                </Box>
                <Box display="flex" alignItems="center">
                  {/* 電話番号入力欄 */}
                  <TextField
                    variant="outlined"
                    size="small"
                    type="tel"
                    value={newEmail?.telNumber ?? ""}
                    label="電話番号"
                    fullWidth
                    placeholder="例）09012345678"
                    disabled={isPhoneVerified}
                    onChange={(e) => {
                      // 入力された文字列から数字以外を除去
                      const numericValue = e.target.value.replace(/\D/g, "");
                      setNewEmail((prev) => ({
                        ...prev,
                        telNumber: numericValue,
                      }));
                    }}
                    sx={{ mt: 1 }}
                  />
                  <Button
                    onClick={() =>
                      handleVerify("phone", newEmail.telNumber ?? "")
                    }
                    disabled={isPhoneVerified || newEmail.telNumber === ""}
                    variant="contained"
                    sx={{ ml: 1, mt: 1 }}
                    size="small"
                  >
                    {isPhoneVerified ? "認証済" : "認証"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pt: 0.5 }}>
          <Button onClick={handleClose}>キャンセル</Button>
          <Button
            onClick={handleAddEmail}
            variant="contained"
            disabled={canRegister}
          >
            登録
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SettingNewMailDialog;
